---
title: "Hedging and Correlation Across Crypto, Gold and Stocks"
slug: hedging-and-correlation
shortTitle: "Hedging & Correlation"
description: "How correlation turns several trades into one bet, what direct and cross hedges really do, what they cost, and how to check your exposure across crypto, gold and stocks."
level: advanced
category: markets
course: advanced
order: 6
topics: [risk-management, commodities, bitcoin, stocks]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/hedging-and-correlation.svg
faqs:
  - q: "What does a correlation of +0.8 mean?"
    a: "It means the two markets have tended to move in the same direction most of the time over the period measured. Holding the same direction in both is then close to holding one larger position. It describes the past and can change."
  - q: "Can I hold a buy and a sell on the same market at the same time?"
    a: "Yes, hedging is allowed on {{BRAND}}, and the order panel shows the margin for matched buy and sell volume as hedged. Keep in mind that both positions still carry costs such as the spread, any trade fee and swap."
  - q: "Is a direct hedge better than closing the position?"
    a: "Usually it locks in roughly the same result as closing, while adding costs and a second decision about when to lift each leg. Some traders use it around a short event, but it does not remove a loss that has already happened."
  - q: "Is gold a hedge against crypto or stocks?"
    a: "Sometimes it has moved differently from them, and sometimes it has moved with them. Correlations between asset classes shift over time, so treat any hedge based on them as partial and check the current relationship rather than assuming it."
  - q: "Where can I see how a market moves with my open trades?"
    a: "The Markets page has a Vs my portfolio column that compares each instrument's recent hourly moves with your open positions, from -1 to +1. Add it from the Columns menu above the table."
---

Three open trades can be three separate ideas, or one idea held three times. If you are long BTC/USDT, long ETH/USDT and long a tech stock, a single risk-off afternoon can hit all three at once, and your total risk is far bigger than any one stop suggests. **Correlation** is the measure of how markets move together, and **hedging** is the attempt to offset one position's risk with another.

Both ideas are useful and both are widely misunderstood. This lesson explains how to read correlation, what direct and cross hedges really do to your profit and loss, why hedges often cost more than they save, and how to check your exposure on {{BRAND}} before you add a trade.

<CTA />

<KeyTakeaways>

- **Correlated trades are one bet.** Two positions that tend to move together can hit their stops on the same day, so count their risk together.
- **A direct hedge freezes a result, it does not fix it.** A buy and a sell of the same size on the same market lock in the current profit or loss while both legs keep costing money.
- **Cross hedges leave gaps.** Hedging with a related market only works as well as the relationship holds, and that can change suddenly.
- **Reducing size is often the cleanest hedge.** Before you add an offsetting trade, ask whether closing part of the original would do the same job for less.

</KeyTakeaways>

## Correlation in plain numbers

A **correlation coefficient** runs from −1 to +1. At +1, two markets have moved in lockstep; at 0, there has been no consistent link; at −1, one has moved up whenever the other moved down. Real markets sit somewhere in between, and the reading depends on the period and timeframe you measure.

Three things are worth remembering:

- **Large crypto coins often move together.** BTC and ETH have frequently shown strong positive correlation, so a long in each is close to one bigger crypto trade.
- **Relationships between asset classes shift.** Crypto, stock indices and gold have at times moved together and at times apart. A relationship that held last quarter is not a rule for next quarter.
- **Correlations can jump in stress.** In sharp sell-offs, many risky markets tend to fall together, which is exactly when diversification is needed most.

<Example title="Two trades, one risk (illustrative prices)">

You buy 0.05 BTC at 60,000 USDT with a stop at 59,100: risk = 900 × 0.05 = **45 USDT**. You also buy 1 ETH at 3,000 with a stop at 2,955: risk = 45 × 1 = **45 USDT**.

On paper, each trade risks 45 USDT. If the two markets fall together, both stops can be hit in the same move, so a realistic view of your risk is 45 + 45 = **90 USDT**. If your plan allows 50 USDT of risk per idea, this pair breaks it.

</Example>

## Three kinds of hedge

### Direct hedge: the same market, the opposite direction

On {{BRAND}} you can hold a buy and a sell on the same market at the same time. When you do, the order panel labels the margin row **Margin required (hedged)** for the matched volume.

{/* TODO: verify with platform — how much margin matched (hedged) volume holds on each market. */}

<Example title="Locking a loss on BTC/USDT (illustrative prices)">

You are long 0.1 BTC from 60,000. The price falls to 57,000, so the position shows (57,000 − 60,000) × 0.1 = **−300 USDT**. You open a **Sell** of 0.1 BTC at 57,000.

- If BTC drops to 55,000: long −500, short +200, net **−300 USDT**.
- If BTC rises to 59,000: long −100, short −200, net **−300 USDT**.

Whatever the price does, the pair stays at about −300 USDT before costs: the same as closing at 57,000. Meanwhile you paid a second spread, a second fee, and possibly swap on both legs for each night they stay open.

</Example>

A direct hedge postpones a decision rather than solving one. Sooner or later you must lift one leg, and that is the same bet on direction you were avoiding. Some traders use it to keep a longer-term position through a short event, but they should compare the cost with simply closing and reopening.

Two platform details matter here. On desktop, **Instant Trade**'s 25%, 50% and 100% close buttons close that share of *every* open position on the market, so they shrink both legs of a hedge together. And if your account reaches {{STOP_OUT_LEVEL}}, positions are closed one at a time starting with the largest loss, which can be one leg of your hedge, leaving the other open on its own.

### Cross hedge: offsetting with a related market

A **cross hedge** uses a different but related market, for example selling a stock index against a holding in one of its member stocks. The size of the hedge depends on how strongly the two have moved together, often summarised as the **hedge ratio** or **beta**.

<Example title="Hedging a stock CFD with an index (illustrative prices)">

You hold 20 shares of a tech stock at 180 USDT: a position value of 20 × 180 = **3,600 USDT**. Say that over recent months the stock moved about 1.3 times as much as NAS100 in percentage terms. A matching index sell would be worth about 3,600 × 1.3 = **4,680 USDT**.

- If the index falls 2% and the stock falls 2.6%, the stock loses 3,600 × 0.026 = 93.60 USDT and the index sell gains 4,680 × 0.02 = 93.60 USDT.
- If the stock drops 6% on its own earnings while the index is flat, you lose 3,600 × 0.06 = **216 USDT** and the hedge does nothing.

</Example>

The second case is **basis risk**: the hedge and the position stop moving together. Cross hedges remove some market-wide risk, never the risk specific to your trade.

### Diversification: spreading across asset classes

Holding positions across crypto, gold, stocks and forex can lower the chance that everything moves against you at once. It is a partial hedge at best, and only while the markets keep behaving differently. [Fundamental drivers](/academy/article/fundamental-drivers) such as interest-rate news can push very different markets in the same direction on the same day.

<CompareTable caption="Ways to reduce exposure compared">

| | What it does | Extra cost | What can go wrong |
| --- | --- | --- | --- |
| Reduce size | Cuts exposure directly | One closing spread and fee | You give up part of a trade you still like |
| Direct hedge | Freezes current profit or loss | Spread, fee and swap on a second leg | Lifting one leg reopens the full bet |
| Cross hedge | Offsets market-wide moves | Spread, fee and swap on another market | The relationship breaks (basis risk) |
| Diversify | Spreads risk across drivers | None beyond normal trading costs | Correlations rise in a sell-off |

</CompareTable>

<Warning title="A hedge is not a safety net under cross margin">

Every leg of a hedge is still a leveraged position backed by the same account equity. Costs build up on both legs, a hedge can stop working without warning, and a stop-out can close one side and leave you fully exposed on the other. Hedging adds complexity, and complexity tends to add mistakes. Read how margin is shared in [isolated vs cross margin](/academy/article/isolated-vs-cross-margin).

</Warning>

## Checking your exposure on {{BRAND}}

The **Vs my portfolio** column on the [Markets](/markets) page compares each instrument's hourly price changes over the last seven days with your open positions, weighted by size. Add it from **Columns** above the table. It labels readings of +0.5 or more as **Same bet**, +0.2 to +0.5 as **Related**, between −0.2 and +0.2 as **Diversifies**, −0.2 to −0.5 as **Offsets**, and −0.5 or less as **Hedges**. The reading assumes you would *buy*; for a sell, flip the sign.

{/* TODO: verify with platform — Vs my portfolio column labels, thresholds and the buy-side assumption. */}

<Steps>
<Step title="List what you hold">

In **Open Positions**, note each trade's direction and the **Value** column. Group trades in the same market or theme.

</Step>
<Step title="Check the relationship">

Before adding a trade, look at its **Vs my portfolio** reading. "Same bet" means you are adding to risk you already carry.

</Step>
<Step title="Add up risk at the stops">

Treat closely related trades as one position and add their stop-loss risks together. Compare the total with your plan.

</Step>
<Step title="Set a concentration alert">

In **Account → Alerts**, a **Concentration** alert can tell you when one instrument grows past a share of your open positions that you choose.

</Step>
</Steps>

<ProTip title="Hedge with the close button first">

When you want less exposure, compare a hedge with simply closing part of the position. Fewer moving parts means fewer costs and fewer ways for the plan to go wrong.

</ProTip>

## Conclusion

Correlation tells you how many separate bets you really hold; hedging tries to offset one bet with another. Correlated trades should be sized and counted together, direct hedges mostly freeze a result while adding costs, and cross hedges leave basis risk behind. Check how a new trade relates to what you already hold, keep the total risk at your stops within plan, and remember that reducing size is often the simplest hedge of all.
